/*
 * What binds one composer to one tray: the React half of the upload manager.
 *
 * The tray outlives the component (uploadManager.ts), so this hook subscribes rather than owns. It
 * does two things beyond that, and both are about never losing what somebody staged: it seeds an
 * empty tray from the draft that was on disk — which is how chips come back after a reload — and
 * it mirrors every ready file straight back into the draft, so the moment an upload finishes it is
 * already durable.
 */

import { useCallback, useEffect, useSyncExternalStore } from "react"
import type { AttachmentRef, DraftHandle, TargetKey } from "../../drafts/types.ts"
import { useWindowFileDrop } from "./staging.ts"
import type { StagedFile } from "./types.ts"
import { releaseTray, trayFor, type TrayStore } from "./uploadManager.ts"

export interface Attachments {
	tray: TrayStore
	files: readonly StagedFile[]
	/** Stages files from the picker, a drop or a paste. */
	attach: (files: readonly File[]) => void
	/** True while something is still on its way up: the send waits for it. */
	busy: boolean
	/** True while a file has failed and nobody has answered for it yet. */
	blocked: boolean
	/** True while a drag carrying files is over the window. */
	dragging: boolean
	/** Empties the tray once the message has left the composer. */
	clear: () => void
}

/**
 * @param initial the set the surface starts from — the attachments a message being edited already
 * has. It is the counterpart of `initialMarkdown` and follows the same rule: a draft that survived
 * a reload wins over it, because that draft is what the person last decided.
 */
export function useAttachments(
	targetKey: TargetKey,
	draft: DraftHandle,
	initial: readonly AttachmentRef[] = [],
): Attachments {
	const tray = trayFor(targetKey)
	const files = useSyncExternalStore(
		useCallback((listener: () => void) => tray.subscribe(listener), [tray]),
		useCallback(() => tray.files(), [tray]),
	)

	// The mirror is re-pointed rather than re-installed: the draft handle is a fresh object on every
	// keystroke, and the tray only ever needs the latest one.
	const mirror = useCallback((refs: AttachmentRef[]) => draft.setAttachments(refs), [draft])
	useEffect(() => {
		tray.mirrorTo(mirror)
	}, [tray, mirror])

	// The draft answers from IndexedDB some milliseconds after the first render; until `ready` an
	// empty attachment list means "not asked yet" and not "none". A draft that was found on disk
	// decides — including when it decides that there are no attachments any more.
	const ready = draft.ready
	const recovered = draft.recovered
	useEffect(() => {
		if (!ready) return
		tray.seed(recovered === null ? initial : recovered.attachments)
	}, [tray, ready, recovered, initial])

	// An empty tray is let go of when its composer is; one with files in it outlives the component,
	// so walking away from a conversation never aborts an upload.
	useEffect(() => () => releaseTray(targetKey), [targetKey])

	const attach = useCallback((next: readonly File[]) => tray.attach(next), [tray])
	const dragging = useWindowFileDrop(attach)

	return {
		tray,
		files,
		attach,
		busy: tray.busy(),
		blocked: tray.blocked(),
		dragging,
		clear: useCallback(() => tray.clear(), [tray]),
	}
}
